import React, { useState } from 'react'

import type { Unit } from '@/lib/ruler/types/unit'
import { formatPointsAsUnit, getUnitSuffix } from '@/lib/ruler/format-unit'
import type { Guide } from '@/lib/guides/types/guide'

/** When set (single-first / single-last), horizontal guides only span this canvas X range. */
export type HorizontalGuideSpan = { left: number; width: number }

type GuidesOverlayProps = {
  guides: Guide[]
  selectedGuideIds: Set<string>
  onGuideDragStart: (
    guideId: string,
    shiftKey: boolean,
    altKey: boolean,
    e: React.PointerEvent
  ) => void
  rulerUnit?: Unit | null
  horizontalZeroAtCanvasX?: number
  verticalZeroAtCanvasY?: number
  scale?: number
  horizontalGuideSpan?: HorizontalGuideSpan | null
  /**
   * When true, guide hitboxes use `pointer-events: none` so pointers reach layers below (e.g. text
   * at z-index 3 under this overlay at z-index 4). Parent `pointer-events: none` does not override
   * descendants that set `auto`, so this must be applied per guide.
   */
  suspendPointerInteraction?: boolean
}

const HITBOX_SIZE = 9
const HITBOX_OFFSET = Math.floor(HITBOX_SIZE / 2)

const GuidesOverlay: React.FC<GuidesOverlayProps> = ({
  guides,
  selectedGuideIds,
  onGuideDragStart,
  rulerUnit,
  horizontalZeroAtCanvasX,
  verticalZeroAtCanvasY,
  scale = 1,
  horizontalGuideSpan = null,
  suspendPointerInteraction = false,
}) => {
  const [hoveredGuideId, setHoveredGuideId] = useState<string | null>(null)
  const effectiveScale = Number.isFinite(scale) && scale > 0 ? scale : 1
  const pixelsPerCanvasUnit =
    effectiveScale * (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1)
  const hairlineThickness = 1 / pixelsPerCanvasUnit
  const snapToDevicePixel = (value: number) =>
    Math.round(value * pixelsPerCanvasUnit) / pixelsPerCanvasUnit

  const getGuideDisplayPosition = React.useCallback(
    (guide: Guide) => {
      const pos = Number.isFinite(guide.position) ? guide.position : 0

      if (guide.type === 'vertical' && Number.isFinite(horizontalZeroAtCanvasX)) {
        return pos - horizontalZeroAtCanvasX!
      }

      if (guide.type === 'horizontal' && Number.isFinite(verticalZeroAtCanvasY)) {
        return pos - verticalZeroAtCanvasY!
      }

      return pos
    },
    [horizontalZeroAtCanvasX, verticalZeroAtCanvasY]
  )

  return (
    <>
      {guides.map((guide) => {
        const isSelected = selectedGuideIds.has(guide.id)
        const isHovered = hoveredGuideId === guide.id && !isSelected
        const isHorizontal = guide.type === 'horizontal'
        const pos = Number.isFinite(guide.position) ? guide.position : 0
        const snappedPos = snapToDevicePixel(pos)
        const displayPos = getGuideDisplayPosition(guide)

        const horizontalSpanStyle =
          isHorizontal && horizontalGuideSpan
            ? {
                left: horizontalGuideSpan.left,
                width: horizontalGuideSpan.width,
                right: 'auto' as const,
              }
            : isHorizontal
              ? { left: 0, right: 0 }
              : { top: 0, bottom: 0 }

        return (
          <div
            key={guide.id}
            className={
              isHorizontal
                ? horizontalGuideSpan
                  ? 'absolute cursor-ns-resize'
                  : 'absolute left-0 right-0 cursor-ns-resize'
                : 'absolute top-0 bottom-0 cursor-ew-resize'
            }
            style={{
              ...horizontalSpanStyle,
              [isHorizontal ? 'top' : 'left']: snappedPos - HITBOX_OFFSET,
              [isHorizontal ? 'height' : 'width']: HITBOX_SIZE,
              zIndex: 20,
              pointerEvents: suspendPointerInteraction ? 'none' : 'auto',
            }}
            onPointerEnter={
              suspendPointerInteraction ? undefined : () => setHoveredGuideId(guide.id)
            }
            onPointerLeave={
              suspendPointerInteraction
                ? undefined
                : () => setHoveredGuideId((prev) => (prev === guide.id ? null : prev))
            }
            onPointerDown={
              suspendPointerInteraction
                ? undefined
                : (e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onGuideDragStart(guide.id, e.shiftKey, e.altKey, e)
                  }
            }
          >
            {/* The visible guide line */}
            <div
              data-guide-id={guide.id}
              data-selected={isSelected}
              data-hovered={isHovered}
              className="doc-guide-line absolute"
              style={
                isHorizontal
                  ? { top: HITBOX_OFFSET, left: 0, right: 0, height: hairlineThickness }
                  : { left: HITBOX_OFFSET, top: 0, bottom: 0, width: hairlineThickness }
              }
            />
            {/* Position label: selected (e.g. just placed) or hovered — must sit above page fill (parent z-index). */}
            {(isSelected || isHovered) && (
              <div
                className="absolute bg-page-editor-ruler-guide text-white text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none font-mono"
                style={
                  isHorizontal
                    ? { top: HITBOX_OFFSET - 20, left: 2 }
                    : { top: 2, left: HITBOX_OFFSET + 4 }
                }
              >
                {isHorizontal ? 'Y' : 'X'}:{' '}
                {rulerUnit
                  ? `${formatPointsAsUnit(displayPos, rulerUnit)} ${getUnitSuffix(rulerUnit)}`
                  : `${Math.round(displayPos)}px`}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

export default GuidesOverlay
